// S4 — SHIFT WASTE SCOREBOARD, /waste/shifts (Module 05 §4, slice 05-B).
//
// Per (facility, date): one column per shift from the §2.15 rollups
// (`group_by=shift`), joined to the shift CODES through module 04's own
// scoreboard read (e17 — same audience: §4 S4's roles all hold
// `production.scoreboard.read`, and the join degrades to raw ids for a custom
// role that does not). The number this screen exists for is HANDED-OFF COST —
// waste that landed on one shift but was CAUSED by another (§2.15's
// `handed_off_cost_minor`), the day-shift-dumps-on-night-shift game in tenge.
//
// THE UNATTRIBUTED COLUMN IS THE D-102 RULE MADE VISIBLE: manual/office
// events carry NO shift, so their cost appears in its own bucket — on the
// day, off every shift's scoreboard. Rendering them under whatever shift
// contained the backfill instant is precisely the fabrication D-102 forbids.
//
// KIOSK (`?kiosk=1`) rides module 04's D-087 wall-mode ruling verbatim: an
// ordinary signed-in session, auto-refresh 60 s, no auth'd actions on screen —
// no token machinery invented. Dates derive from the FACILITY calendar
// (D-089), never the browser's.
//
// MONEY: per §9.14 the buckets are per-currency; sums here stay within one
// facility (one functional currency). A redacted payload (no `*_minor` keys)
// renders the mark on every money cell — count columns stay real.
//
// UX-4 — the columns are §3.6 `Card`s on the register (the hand-rolled
// `.waste-shift-card` frame carried its own border, its own dashed variant and
// its own 1rem/1.25rem type), and the week strip's cell was corrected:
//
//   THE SEVEN-DAY STRIP PRINTED THREE DIFFERENT QUANTITIES IN ONE UNLABELLED
//   SLOT. A day with money showed money; a day whose money was REDACTED showed
//   the bare event COUNT; a day with no rollup bucket at all showed the literal
//   string "0". So "12" could mean twelve tenge-thousands or twelve events, and
//   a day with nothing booked was indistinguishable from a day whose total
//   happened to be zero — the "a computed zero and an unknown must not render
//   identically" rule, broken twice in one cell. Each case now says what it is:
//   money with its currency, a count with the word "events", and "None" for a
//   day the plant booked no waste at all.

import { useMemo, useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { requestIdOf } from "../../../../lib/request";
import { RefLabel } from "../../../../components/ref-label";
import { FacilityFilter } from "../../production/components/facility-filter";
import { useFacilities } from "../../production/hooks/production-queries";
import { formatMinor, formatQty, localeLabel } from "../../production/lib/format";
import { moneyCell } from "./events";
import { isWasteReadRefused, useWastePermissions } from "../hooks/permissions";
import { useShiftLabels } from "../hooks/shift-labels";
import { useWasteCatalog, useWasteRollups, type WasteRollupRow } from "../hooks/waste-queries";

export interface WasteShiftsSearch {
  kiosk?: string;
  facility_id?: string;
}

/** TanStack Router JSON-parses search values, so the spec's own URL
 * `?kiosk=1` arrives as the NUMBER 1 — a string-only check made kiosk mode
 * unreachable by that URL (surface-review finding, 2026-08-09). Both
 * spellings are accepted and normalized to the string "1". `facility_id`
 * survives validation so a wall tablet can be POINTED at a facility — kiosk
 * hides the pickers, so without the URL param the mode could only ever show
 * the first-listed facility. */
export function validateWasteShiftsSearch(search: Record<string, unknown>): WasteShiftsSearch {
  const out: WasteShiftsSearch = {};
  const kiosk = search["kiosk"];
  if (kiosk === "1" || kiosk === 1) out.kiosk = "1";
  const facility = search["facility_id"];
  if (typeof facility === "string" && facility.length > 0) out.facility_id = facility;
  return out;
}

export interface ShiftColumn {
  /** null = the D-102 unattributed bucket (manual/office sources). */
  readonly shiftInstanceId: string | null;
  readonly eventCount: number;
  /** undefined = redacted (absent keys) — never 0. */
  readonly costMinor: number | undefined;
  readonly handedOffMinor: number | undefined;
  readonly currency: string;
  /** Per-UoM quantity lines (never summed across UoM — §9.14). */
  readonly qtyByUom: readonly { uom: string; qty: string }[];
}

/** Fold the per-(shift, uom, currency) buckets into shift columns. Money sums
 * only when EVERY contributing bucket carries it — a partially redacted set
 * must not render a fabricated partial total. */
export function shiftColumnsOf(rows: readonly WasteRollupRow[]): ShiftColumn[] {
  const byShift = new Map<string | null, WasteRollupRow[]>();
  for (const row of rows) {
    const key = row.shift_instance_id ?? null;
    const list = byShift.get(key) ?? [];
    list.push(row);
    byShift.set(key, list);
  }
  const columns: ShiftColumn[] = [];
  for (const [shiftInstanceId, list] of byShift) {
    const allMoney = list.every((row) => row.total_cost_minor !== undefined);
    const qtyByUom = new Map<string, bigint>();
    for (const row of list) {
      const [whole = "0", frac = ""] = row.total_qty.split(".");
      const milli =
        BigInt(whole.replace("-", "") || "0") * 1000n + BigInt((frac + "000").slice(0, 3));
      const signed = row.total_qty.startsWith("-") ? -milli : milli;
      qtyByUom.set(row.uom, (qtyByUom.get(row.uom) ?? 0n) + signed);
    }
    columns.push({
      shiftInstanceId,
      eventCount: list.reduce((sum, row) => sum + row.event_count, 0),
      costMinor: allMoney
        ? list.reduce((sum, row) => sum + (row.total_cost_minor ?? 0), 0)
        : undefined,
      handedOffMinor: allMoney
        ? list.reduce((sum, row) => sum + (row.handed_off_cost_minor ?? 0), 0)
        : undefined,
      currency: list[0]?.currency ?? "",
      qtyByUom: [...qtyByUom.entries()].map(([uom, milli]) => ({
        uom,
        qty: `${milli < 0n ? "-" : ""}${(milli < 0n ? -milli : milli) / 1000n}.${((milli < 0n ? -milli : milli) % 1000n).toString().padStart(3, "0")}`,
      })),
    });
  }
  // Real shifts first (stable by id), the unattributed bucket last.
  return columns.sort((a, b) => {
    if (a.shiftInstanceId === null) return 1;
    if (b.shiftInstanceId === null) return -1;
    return a.shiftInstanceId < b.shiftInstanceId ? -1 : 1;
  });
}

/** The ISO date `days` before an ISO date (pure calendar arithmetic). */
export function isoDaysBefore(dateIso: string, days: number): string {
  const stamp = Date.parse(`${dateIso}T00:00:00Z`);
  return new Date(stamp - days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * What ONE cell of the seven-day strip is claiming. Three different quantities
 * used to share the slot with nothing to tell them apart (see the file header):
 *
 *  - `none`  — the day has no rollup bucket at all: the plant booked no waste.
 *  - `money` — the day's total cost, which renders WITH its currency.
 *  - `count` — the day's event count, shown only when the money is REDACTED,
 *              and labelled as a count so it can never be read as money.
 */
export type WeekCell =
  | { readonly kind: "none" }
  | { readonly kind: "money"; readonly minor: number; readonly currency: string }
  | { readonly kind: "count"; readonly count: number };

export function weekCellOf(
  day: { count: number; costMinor: number | undefined; currency: string } | undefined,
): WeekCell {
  if (day === undefined) return { kind: "none" };
  if (day.costMinor === undefined) return { kind: "count", count: day.count };
  return { kind: "money", minor: day.costMinor, currency: day.currency };
}

export function WasteShiftsScreen() {
  const { t, i18n } = useTranslation();
  const search = useSearch({ from: "/waste/shifts" });
  const kiosk = search.kiosk === "1";

  const perms = useWastePermissions();
  const refused = isWasteReadRefused(perms, "waste.read");

  const facilities = useFacilities();
  // Seeded from the URL (S2's pattern): a kiosk tablet is pointed at a
  // facility via `?kiosk=1&facility_id=…` — the pickers are hidden in kiosk
  // mode, so the URL is the only way to target facility A/C on a wall.
  const [facilityId, setFacilityId] = useState<string | undefined>(search.facility_id);
  const effectiveFacility = facilityId ?? facilities.data?.items[0]?.id;
  const facilityTimezone = facilities.data?.items.find((f) => f.id === effectiveFacility)?.timezone;
  const [pickedDate, setPickedDate] = useState<string | undefined>(undefined);
  // D-089: the DEFAULT date is the facility's today, never the browser's.
  const date = pickedDate ?? facilityTodayIso(Date.now(), facilityTimezone);

  const refetchMs = kiosk ? 60_000 : undefined;
  // A refused read is not issued: this screen fires THREE rollups, so the
  // react-query default turned one refusal into twelve requests over ~8 s.
  const readEnabled = effectiveFacility !== undefined && !refused;
  const rollups = useWasteRollups(
    {
      group_by: "shift",
      from: date,
      to: date,
      ...(effectiveFacility !== undefined ? { facility_id: effectiveFacility } : {}),
    },
    readEnabled,
    refetchMs,
  );
  const week = useWasteRollups(
    {
      group_by: "day",
      from: isoDaysBefore(date, 6),
      to: date,
      ...(effectiveFacility !== undefined ? { facility_id: effectiveFacility } : {}),
    },
    readEnabled,
    refetchMs,
  );
  const dayCategories = useWasteRollups(
    {
      group_by: "category",
      from: date,
      to: date,
      ...(effectiveFacility !== undefined ? { facility_id: effectiveFacility } : {}),
    },
    readEnabled,
    refetchMs,
  );
  // The shift CODES for the columns — module 04's own board, same audience,
  // through the ONE shared hook. This screen used to read the picked day ALONE:
  // §9.7 dates a shift by the day it STARTED, so a night shift running
  // 22:00 → 06:00 sits on YESTERDAY's board all through the small hours, and
  // the board headed it with an 8-character id instead of NIGHT. Measured: a
  // 06:17 Almaty event whose own station screen said `NIGHT / 2026-08-12` was
  // headed `019ff808` here, because this screen asked only about 2026-08-13.
  const shifts = useShiftLabels(refused ? undefined : effectiveFacility, date);
  const catalog = useWasteCatalog();

  const columns = useMemo(() => shiftColumnsOf(rollups.data ?? []), [rollups.data]);
  const categoryName = (id: string | undefined): string | undefined => {
    if (id === undefined) return undefined;
    const row = catalog.data?.categories.find((c) => c.id === id);
    return row === undefined ? undefined : localeLabel(row.name_i18n, i18n.language) || row.code;
  };

  const topCategories = useMemo(() => {
    const rows = [...(dayCategories.data ?? [])];
    const allMoney = rows.every((row) => row.total_cost_minor !== undefined);
    rows.sort((a, b) =>
      allMoney
        ? (b.total_cost_minor ?? 0) - (a.total_cost_minor ?? 0)
        : b.event_count - a.event_count,
    );
    return rows.slice(0, 3);
  }, [dayCategories.data]);

  const weekByDate = useMemo(() => {
    const map = new Map<
      string,
      { count: number; costMinor: number | undefined; currency: string }
    >();
    for (const row of week.data ?? []) {
      const key = row.rollup_date ?? "";
      const prior = map.get(key) ?? { count: 0, costMinor: 0, currency: row.currency };
      map.set(key, {
        count: prior.count + row.event_count,
        // A single redacted bucket redacts the day — never a partial total.
        costMinor:
          prior.costMinor === undefined || row.total_cost_minor === undefined
            ? undefined
            : prior.costMinor + row.total_cost_minor,
        currency: row.currency,
      });
    }
    return map;
  }, [week.data]);

  return (
    <section
      className={kiosk ? "waste-shifts-screen waste-kiosk" : "waste-shifts-screen"}
      data-testid="waste-shifts-screen"
    >
      <PageHeader
        title={t("waste.shifts.title")}
        actions={
          <>
            {kiosk ? (
              <StatusChip variant="accent" data-testid="waste-kiosk-badge">
                {t("waste.shifts.kioskBadge")}
              </StatusChip>
            ) : null}
          </>
        }
      />

      {/* KIOSK MODE HAS TO SAY WHAT IT IS SHOWING. The pickers are hidden by
          design, and the board printed no substitute: on a wall in a
          three-facility group, "Shift waste scoreboard" is unattributable at a
          glance — and a glance is the only thing a wall board gets. The
          facility and the business date are the two facts the hidden controls
          used to carry. */}
      {kiosk ? (
        <p className="waste-hint" data-testid="waste-kiosk-context">
          {t("waste.shifts.kioskContext", {
            facility:
              facilities.data?.items.find((f) => f.id === effectiveFacility)?.name ??
              t("waste.shifts.kioskFacilityUnknown"),
            date,
          })}
        </p>
      ) : null}

      {!kiosk ? (
        <div className="filter-bar" data-testid="waste-shifts-filters">
          <FacilityFilter
            value={effectiveFacility ?? ""}
            facilities={facilities.data?.items ?? []}
            testId="waste-shifts-facility"
            onChange={setFacilityId}
          />
          <Field label={t("waste.shifts.date")}>
            <input
              type="date"
              value={date}
              data-testid="waste-shifts-date"
              onChange={(e) => {
                setPickedDate(e.target.value);
              }}
            />
          </Field>
        </div>
      ) : null}

      {refused ? (
        <ForbiddenState title={t("waste.access.title")} body={t("waste.access.ledger")} />
      ) : null}

      {!refused && rollups.fetchStatus === "paused" && (rollups.data?.length ?? 0) === 0 ? (
        <OfflineState title={t("waste.offline.title")} body={t("waste.offline.body")} />
      ) : null}

      {!refused && rollups.isPending && rollups.fetchStatus !== "paused" && readEnabled ? (
        <TableSkeleton rows={3} label={t("waste.common.loading")} />
      ) : null}

      {!refused && rollups.error !== null ? (
        <ErrorState
          title={t("waste.shifts.error")}
          retryLabel={t("waste.common.retry")}
          requestIdLabel={t("waste.common.requestId")}
          requestId={requestIdOf(rollups.error)}
          onRetry={() => {
            void rollups.refetch();
          }}
        />
      ) : null}

      {!refused &&
      !rollups.isPending &&
      rollups.fetchStatus !== "paused" &&
      rollups.error === null &&
      columns.length === 0 ? (
        <EmptyState title={t("waste.shifts.emptyTitle")} body={t("waste.shifts.emptyBody")} />
      ) : null}

      {columns.length > 0 ? (
        <>
          <div className="waste-shift-columns" data-testid="waste-shift-columns">
            {columns.map((column) => (
              <Card
                key={column.shiftInstanceId ?? "unattributed"}
                {...(column.shiftInstanceId === null
                  ? { className: "waste-shift-card-unattributed" }
                  : {})}
                title={
                  column.shiftInstanceId === null ? (
                    t("waste.shifts.unattributed")
                  ) : (
                    // The shift's own code, resolving marker, or short id — a
                    // full uuid is never a column heading.
                    <RefLabel
                      value={shifts.labelOf(column.shiftInstanceId)}
                      id={column.shiftInstanceId}
                      pending={shifts.isPending}
                    />
                  )
                }
                data-testid={
                  column.shiftInstanceId === null ? "waste-shift-unattributed" : "waste-shift-card"
                }
              >
                {column.shiftInstanceId === null ? (
                  <p className="waste-hint">{t("waste.shifts.unattributedNote")}</p>
                ) : null}
                <dl className="waste-shift-facts">
                  <dt>{t("waste.shifts.cost")}</dt>
                  <dd data-testid="waste-shift-cost">
                    {column.costMinor === undefined
                      ? "•••"
                      : formatMinor(column.costMinor, column.currency, i18n.language)}
                  </dd>
                  <dt>{t("waste.shifts.events")}</dt>
                  <dd>{column.eventCount}</dd>
                  <dt>{t("waste.shifts.handedOff")}</dt>
                  <dd data-testid="waste-shift-handed-off">
                    {column.handedOffMinor === undefined
                      ? "•••"
                      : formatMinor(column.handedOffMinor, column.currency, i18n.language)}
                  </dd>
                  {column.qtyByUom.map((line) => (
                    <div key={line.uom} className="waste-shift-qty">
                      <dt>{line.uom}</dt>
                      <dd>{formatQty(line.qty, { locale: i18n.language })}</dd>
                    </div>
                  ))}
                </dl>
                {/* THE NUMBER OPENS. The handed-off figure is the reason this
                    screen exists and it drilled nowhere: the page's one link
                    carried the facility and the day and never the SHIFT, even
                    though the ledger's `shift_instance_id` filter survives URL
                    validation expressly so this board can point at it. Kiosk
                    mode has no links by ruling (D-087: no auth'd actions on a
                    wall), so the card stays inert there. */}
                {!kiosk && column.shiftInstanceId !== null && effectiveFacility !== undefined ? (
                  <p className="waste-drill">
                    <Link
                      to="/waste/events"
                      data-testid="waste-shift-drill"
                      search={{
                        facility_id: effectiveFacility,
                        occurred_from: date,
                        occurred_to: date,
                        shift_instance_id: column.shiftInstanceId,
                      }}
                    >
                      {t("waste.shifts.drillShift")}
                    </Link>
                  </p>
                ) : null}
              </Card>
            ))}
          </div>

          {topCategories.length > 0 ? (
            <div className="waste-top-categories" data-testid="waste-top-categories">
              <h2>{t("waste.shifts.topCategories")}</h2>
              <ol>
                {topCategories.map((row) => (
                  <li key={`${row.category_id ?? ""}:${row.uom}:${row.currency}`}>
                    <RefLabel
                      value={categoryName(row.category_id)}
                      id={row.category_id}
                      pending={catalog.isPending}
                    />{" "}
                    · {moneyCell(row.total_cost_minor, row.currency, i18n.language)}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Week heatmap: totals per facility business date. */}
      {(week.data?.length ?? 0) > 0 ? (
        <div className="waste-week" data-testid="waste-week-heatmap">
          <h2>{t("waste.shifts.week")}</h2>
          <div className="waste-week-cells">
            {Array.from({ length: 7 }, (_, index) => {
              const cellDate = isoDaysBefore(date, 6 - index);
              const cell = weekCellOf(weekByDate.get(cellDate));
              return (
                <div key={cellDate} className="waste-week-cell" data-testid="waste-week-cell">
                  <span className="waste-week-date">{cellDate.slice(5)}</span>
                  {cell.kind === "none" ? (
                    <span className="waste-week-none">{t("waste.shifts.weekNone")}</span>
                  ) : (
                    <span className="waste-week-value">
                      {cell.kind === "money"
                        ? formatMinor(cell.minor, cell.currency, i18n.language)
                        : t("waste.shifts.weekEvents", { count: cell.count })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {!kiosk ? (
        <p className="waste-drill" data-testid="waste-shifts-drill">
          <Link
            to="/waste/events"
            search={{
              ...(effectiveFacility !== undefined ? { facility_id: effectiveFacility } : {}),
              occurred_from: date,
              occurred_to: date,
            }}
          >
            {t("waste.shifts.drillLedger")}
          </Link>
        </p>
      ) : null}
    </section>
  );
}
